// Removed socket.io-client import - using native WebSocket instead
import { store } from '@store/index';

export interface WebSocketConfig {
  url: string;
  reconnection?: boolean;
  reconnectionAttempts?: number;
  reconnectionDelay?: number;
  reconnectionDelayMax?: number;
  timeout?: number;
  autoConnect?: boolean;
}

export interface WebSocketMessage {
  type: string;
  data: any;
  timestamp: Date;
}

export class WebSocketService {
  private socket: WebSocket | null = null;
  private clientId: string;
  private eventHandlers: Map<string, Set<(data: any) => void>> = new Map();
  private connectionListeners: Set<(connected: boolean) => void> = new Set();
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 1000;
  private pingInterval: NodeJS.Timeout | null = null;
  private lastPingTime: number = 0;
  private lastLatency: number = 0;

  constructor() {
    this.clientId = `client-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.handleOpen = this.handleOpen.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handleMessage = this.handleMessage.bind(this);
  }

  connect(config: WebSocketConfig): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      console.warn('WebSocket already connected');
      return;
    }

    const token = store.getState().auth.token;
    if (!token) {
      console.error('No authentication token available');
      return;
    }

    // Construct WebSocket URL with authentication
    // If the URL already has /ws, don't add it again
    let wsUrl = config.url.replace('http://', 'ws://').replace('https://', 'wss://');

    // Ensure we have the right WebSocket endpoint with API path
    if (!wsUrl.includes('/api/v1/ws')) {
      // Remove trailing slash
      wsUrl = wsUrl.replace(/\/$/, '');
      // Add API path if not already present
      if (!wsUrl.includes('/api/v1')) {
        wsUrl = wsUrl + '/api/v1';
      }
      wsUrl = wsUrl + '/ws';
    }

    const url = `${wsUrl}/${this.clientId}?token=${encodeURIComponent(token)}`;

    try {
      this.socket = new WebSocket(url);
      this.setupEventListeners();
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      this.handleError(new Event('error'));
    }
  }

  disconnect(): void {
    this.stopPingInterval();
    this.clearReconnectTimeout();

    if (this.socket) {
      this.socket.removeEventListener('open', this.handleOpen);
      this.socket.removeEventListener('close', this.handleClose);
      this.socket.removeEventListener('error', this.handleError);
      this.socket.removeEventListener('message', this.handleMessage);

      if (this.socket.readyState === WebSocket.OPEN) {
        this.socket.close();
      }
      this.socket = null;
    }

    this.eventHandlers.clear();
    this.connectionListeners.clear();
    this.reconnectAttempts = 0;
  }

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  getLatency(): number {
    return this.lastLatency;
  }

  emit(event: string, data: any): void {
    if (!this.isConnected()) {
      console.error('Cannot emit event: WebSocket not connected');
      return;
    }

    const message = JSON.stringify({
      type: event,
      data,
      timestamp: new Date().toISOString(),
    });

    this.socket!.send(message);
  }

  on(event: string, handler: (data: any) => void): () => void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }

    this.eventHandlers.get(event)!.add(handler);

    return () => {
      const handlers = this.eventHandlers.get(event);
      if (handlers) {
        handlers.delete(handler);
        if (handlers.size === 0) {
          this.eventHandlers.delete(event);
        }
      }
    };
  }

  onConnectionChange(listener: (connected: boolean) => void): () => void {
    this.connectionListeners.add(listener);
    listener(this.isConnected());

    return () => {
      this.connectionListeners.delete(listener);
    };
  }

  reconnect(): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      return;
    }

    this.disconnect();
    // Use the same URL logic as the initial connection
    const wsUrl = import.meta.env.VITE_WS_URL ||
      `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`;

    this.connect({
      url: wsUrl,
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay,
    });
  }

  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.addEventListener('open', this.handleOpen);
    this.socket.addEventListener('close', this.handleClose);
    this.socket.addEventListener('error', this.handleError);
    this.socket.addEventListener('message', this.handleMessage);
  }

  private handleOpen(): void {
    console.log('WebSocket connected');
    this.reconnectAttempts = 0;
    this.notifyConnectionListeners(true);
    this.startPingInterval();

    // Send initial connection message
    this.emit('connection', { clientId: this.clientId });
  }

  private handleClose(event: CloseEvent): void {
    console.log('WebSocket disconnected:', event.reason || 'No reason provided');
    this.notifyConnectionListeners(false);
    this.stopPingInterval();

    // Attempt to reconnect if not intentionally closed
    if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
      this.scheduleReconnect();
    }
  }

  private handleError(event: Event): void {
    console.error('WebSocket error:', event);
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const message = JSON.parse(event.data);

      // Handle different message types
      if (message.type === 'pong') {
        this.lastLatency = Date.now() - this.lastPingTime;
        return;
      }

      if (message.type === 'heartbeat') {
        // Server-initiated heartbeat, respond with pong
        this.emit('pong', { timestamp: message.timestamp });
        return;
      }

      // Handle regular events
      const handlers = this.eventHandlers.get(message.type);
      if (handlers) {
        handlers.forEach(handler => {
          try {
            handler(message.data || message);
          } catch (error) {
            console.error(`Error in WebSocket event handler for ${message.type}:`, error);
          }
        });
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  }

  private notifyConnectionListeners(connected: boolean): void {
    this.connectionListeners.forEach(listener => {
      try {
        listener(connected);
      } catch (error) {
        console.error('Error in connection listener:', error);
      }
    });
  }

  private startPingInterval(): void {
    this.pingInterval = setInterval(() => {
      if (this.isConnected()) {
        this.lastPingTime = Date.now();
        this.emit('ping', { timestamp: this.lastPingTime });
      }
    }, 30000); // Every 30 seconds
  }

  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private scheduleReconnect(): void {
    this.clearReconnectTimeout();

    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts),
      30000 // Max 30 seconds
    );

    console.log(`Scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectAttempts++;
      this.reconnect();
    }, delay);
  }

  private clearReconnectTimeout(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }
}

export const webSocketService = new WebSocketService();

// Make websocket service available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).webSocketService = webSocketService;
}

export default webSocketService;
